import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { AssetDocument } from '@/types/models';

// Fetch all documents for an asset
export function useAssetDocuments(organizationId: string, assetId: string) {
  return useQuery({
    queryKey: ['assetDocuments', organizationId, assetId],
    queryFn: async (): Promise<AssetDocument[]> => {
      const { data, error } = await supabase
        .from('asset_documents')
        .select(`
          *,
          document_type:document_types(id, name, color, icon)
        `)
        .eq('organization_id', organizationId)
        .eq('asset_id', assetId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId && !!assetId,
  });
}

// Fetch all documents for an organization
export function useOrganizationDocuments(organizationId: string) {
  return useQuery({
    queryKey: ['organizationDocuments', organizationId],
    queryFn: async (): Promise<AssetDocument[]> => {
      const { data, error } = await supabase
        .from('asset_documents')
        .select(`
          *,
          asset:assets(description, tag_id),
          document_type:document_types(id, name, color, icon)
        `)
        .eq('organization_id', organizationId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
  });
}

// Upload a document
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      assetId,
      file,
      description,
      userId,
      documentTypeId,
    }: {
      organizationId: string;
      assetId: string;
      file: File;
      description: string;
      userId: string;
      documentTypeId?: string;
    }) => {
      // Generate a unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${organizationId}/${assetId}/${Date.now()}.${fileExt}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('asset-documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('asset-documents')
        .getPublicUrl(fileName);

      // Create document record
      const { data, error } = await supabase
        .from('asset_documents')
        .insert({
          organization_id: organizationId,
          asset_id: assetId,
          description,
          file_type: file.type,
          file_name: file.name,
          file_url: publicUrl,
          file_size: file.size,
          uploaded_by: userId,
          document_type_id: documentTypeId || null,
        })
        .select(`
          *,
          document_type:document_types(id, name, color, icon)
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assetDocuments', variables.organizationId, variables.assetId] });
      queryClient.invalidateQueries({ queryKey: ['organizationDocuments', variables.organizationId] });
    },
  });
}

// Delete a document
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      organizationId,
      assetId,
      fileUrl,
    }: {
      documentId: string;
      organizationId: string;
      assetId: string;
      fileUrl: string;
    }) => {
      // Extract file path from URL
      const filePath = fileUrl.split('/asset-documents/')[1];

      // Delete from storage
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('asset-documents')
          .remove([filePath]);

        if (storageError) console.error('Error deleting file from storage:', storageError);
      }

      // Delete document record
      const { error } = await supabase
        .from('asset_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assetDocuments', variables.organizationId, variables.assetId] });
      queryClient.invalidateQueries({ queryKey: ['organizationDocuments', variables.organizationId] });
    },
  });
}

// Download a document (get signed URL for private documents)
export async function getDocumentDownloadUrl(fileUrl: string): Promise<string> {
  // Extract file path from URL
  const filePath = fileUrl.split('/asset-documents/')[1];

  if (!filePath) {
    throw new Error('Invalid file URL');
  }

  // For private bucket, create a signed URL
  const { data, error } = await supabase.storage
    .from('asset-documents')
    .createSignedUrl(filePath, 3600); // 1 hour expiry

  if (error) throw error;
  return data.signedUrl;
}
